import { auth } from './firebaseConfig';
import { getApiBaseUrl } from '../config/apiBase';

declare const __DEV__: boolean;

/** The code every authenticated endpoint answers a missing or bad token with. */
export const AUTH_REQUIRED_CODE = 'auth-required';
/** Our own code for a request that never landed. Shares the `ImportErrorCode` word. */
export const NETWORK_ERROR_CODE = 'network';

/**
 * An error carrying the machine-readable `code` a route returned alongside its
 * human `error` string, so callers can branch on the reason rather than
 * pattern-matching prose.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Firebase codes that mean the session itself is gone, not the network. The
 * SDK signs the user out on these; a request on their behalf is a request for
 * nobody.
 */
const REVOKED_SESSION_CODES = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
]);

/**
 * A signed-in cook whose ID token could not be produced (#778).
 *
 * Firebase keeps a restored user when it cannot refresh an expired token — a
 * tablet waking on a slow network — so the app looks signed in while every
 * request would go out bare and be answered 401, which three screens then read
 * as "you have been signed out". This is thrown instead, before any request.
 *
 * It extends `ApiError` so every existing catch reads it the same way:
 *  - `transient` (the refresh never landed, or an unknown failure) carries
 *    `code: 'network'` and `status: 0` — the server was never asked — and
 *    reads as a network failure with a Retry.
 *  - `revoked` (the session is gone) carries `code: 'auth-required'` and
 *    `status: 401`, and reads as "sign in again".
 * Unknown codes are transient on purpose: "try again" is the cheaper mistake.
 */
export class SessionTokenError extends ApiError {
  readonly reason: 'transient' | 'revoked';
  readonly firebaseCode: string;

  constructor(cause: unknown) {
    const firebaseCode = (cause as { code?: unknown } | null)?.code;
    const code = typeof firebaseCode === 'string' ? firebaseCode : 'unknown';
    const revoked = REVOKED_SESSION_CODES.has(code);
    super(
      revoked ? 'Session expired, sign in again' : `Could not refresh the session token (${code})`,
      revoked ? 401 : 0,
      revoked ? AUTH_REQUIRED_CODE : NETWORK_ERROR_CODE,
    );
    this.name = 'SessionTokenError';
    this.reason = revoked ? 'revoked' : 'transient';
    this.firebaseCode = code;
  }
}

/** Duck-typed on `name`: `handleError` and the screens re-wrap the throw. */
export function isSessionTokenError(error: unknown): error is SessionTokenError {
  return !!error && typeof error === 'object' && (error as { name?: unknown }).name === 'SessionTokenError';
}

/**
 * Did the server refuse because nobody is signed in — or would it?
 *
 * A scan, a recipe photo and a recipe fetch all need an account, and the
 * refusal is a sentence the cook can act on ("create an account", "sign in
 * again"), not a fault to retry or to file in error tracking. Duck-typed
 * because `handleError` re-wraps the throw but keeps the code and status. A
 * transient `SessionTokenError` is deliberately not one: that cook is signed in.
 */
export function isAuthRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, status } = error as { code?: unknown; status?: unknown };
  return code === AUTH_REQUIRED_CODE || status === 401;
}

/**
 * Headers for a request to our API, with the cook's ID token when there is one.
 *
 * A guest gets plain headers: some routes serve guests, and the ones that do
 * not answer 401 with `auth-required`. A signed-in cook always gets a token or
 * a `SessionTokenError` — never a bare request (#778).
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // Until Firebase has restored its session, currentUser is null. The root
  // layout holds the screens until then, except on the OAuth-redirect path,
  // which gives up after 5 s. Resolves at once after the first auth state.
  if (auth && typeof auth.authStateReady === 'function') {
    try {
      await auth.authStateReady();
    } catch {
      // read whatever state there is
    }
  }
  const user = auth?.currentUser;
  if (!user) return headers;

  try {
    headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  } catch (error) {
    throw new SessionTokenError(error);
  }
  return headers;
}

export interface ApiPostOptions {
  /**
   * Count a 200 as delivered only when its JSON body says `success: true`.
   * Expo's dev server answers 200 + HTML for an `/api` path it does not know,
   * so `response.ok` alone is not proof the route ran. Throws `ApiError` with
   * code `not-delivered` otherwise.
   */
  requireSuccess?: boolean;
}

export const NOT_DELIVERED_CODE = 'not-delivered';

export async function apiPost<T = any>(path: string, body?: any, options: ApiPostOptions = {}): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new ApiError(
      err.error || `Server returned ${response.status}`,
      response.status,
      err.code
    );
  }
  if (!options.requireSuccess) return response.json();
  const data = await response.json().catch(() => null);
  if (data?.success !== true) {
    throw new ApiError(data?.error || 'The server did not confirm the request', response.status, NOT_DELIVERED_CODE);
  }
  return data;
}

export async function apiGet<T = any>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // Same ApiError as apiPost. It extends Error and keeps the same message, so
    // existing callers are unaffected — but a caller that needs to tell "the
    // server has never heard of this" (404) from "the network blinked" now can.
    throw new ApiError(
      err.error || `Server returned ${response.status}`,
      response.status,
      err.code
    );
  }
  return response.json();
}
